// ── FLATBOX-mötesbokning ───────────────────────────────────────────────────────
// Tolkar ett inklistrat inbokningsmail och bygger ett färdigt Teams-möte som
// antingen skickas via Microsoft Graph (se services/msGraph.js) eller laddas
// ner som .ics-fil att öppna i Outlook.
import { esc, buildSignatureHTML, buildPlainText, loadSignatureContext } from './signatureHtml';

export const MEETING_DEFAULT_MINUTES = 30;
export const MEETING_TIMEZONE = 'Europe/Stockholm';

// Ämnesrad: FLATBOX | Kundnamn
export const SUBJECT_PREFIX = 'FLATBOX';

// Fast brödtext för alla FLATBOX-inbjudningar. Signaturen läggs till separat.
export const FLATBOX_INTRO =
  'Välkommen på möte att få veta mer om granskning med Flatbox enligt BEAst Effektivare granskning.';
export const FLATBOX_TAGLINE =
  'Flatbox | Granskning som fungerar. Data som stannar i Sverige.';
export const FLATBOX_SIGNOFF = 'Mvh';

export function flatboxSubject(customerName) {
  return `${SUBJECT_PREFIX} | ${customerName || ''}`.trim();
}

// Standardtexten utan signatur, stycken skilda av en tomrad. Den kan
// redigeras i "Nytt från bokningsmail" innan den går till buildFlatboxBody.
export function flatboxStandardText(senderName = '') {
  return [FLATBOX_INTRO, '', FLATBOX_TAGLINE, '', FLATBOX_SIGNOFF, senderName].join('\n').trimEnd();
}

// Kallelsens brödtext i HTML + ren text. Varje stycke blir ett <p>, enkla
// radbrytningar <br>. Stycket som är taglinen blir alltid fetstilt, och
// signaturen läggs alltid sist — även när texten har redigerats.
export function buildFlatboxBody({ text = flatboxStandardText(), signatureHtml = '', signatureText = '' }) {
  const p = 'margin:0 0 12px;font-family:Arial,Helvetica,sans-serif;font-size:11pt;color:#333333;';
  const stycken = String(text).split(/\r?\n\s*\r?\n/).map(s => s.trim()).filter(Boolean);
  const bodyHtml = [
    ...stycken.map(st => {
      const inner = esc(st).replace(/\r?\n/g, '<br>');
      return `<p style="${p}">${st === FLATBOX_TAGLINE ? `<strong>${inner}</strong>` : inner}</p>`;
    }),
    signatureHtml,
  ].filter(Boolean).join('\n');

  const bodyText = [stycken.join('\n\n'), '', signatureText].join('\n').trimEnd();
  return { bodyHtml, bodyText };
}

// Avsändarens signatur från samma källa som Signature-sidan. Går den inte att
// läsa blir kallelsen utan signatur, med namnet från inloggningen.
export async function loadMeetingSignature(currentUser) {
  try {
    const { company, dept, user } = await loadSignatureContext(currentUser);
    return {
      html: buildSignatureHTML(company, dept, user, 'desktop', 'landscape'),
      text: buildPlainText(company, dept, user),
      senderName: user.displayName || currentUser.displayName || '',
      senderEmail: user.email || currentUser.email || '',
    };
  } catch {
    return {
      html: '', text: '',
      senderName: currentUser.displayName || '',
      senderEmail: currentUser.email || '',
    };
  }
}

// ── Tolkning av inbokningsmail ────────────────────────────────────────────────

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.]+/i;

const MONTHS = {
  jan: 1, feb: 2, mar: 3, apr: 4, maj: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, okt: 10, nov: 11, dec: 12,
};

function pad(n) { return String(n).padStart(2, '0'); }

// Hittar första datumet i texten. Klarar 2026-08-28, 28/8 2026, 28/8 och
// "28 augusti 2026". Utan årtal antas innevarande år.
export function findDate(str, today = new Date()) {
  if (!str) return '';

  const iso = str.match(/\b(20\d{2})[-/](\d{1,2})[-/](\d{1,2})\b/);
  if (iso) return `${iso[1]}-${pad(iso[2])}-${pad(iso[3])}`;

  const slash = str.match(/\b(\d{1,2})[/.](\d{1,2})(?:[/.\s-](20\d{2}))?\b/);
  if (slash) {
    const year = slash[3] || today.getFullYear();
    return `${year}-${pad(slash[2])}-${pad(slash[1])}`;
  }

  const written = str.match(/\b(\d{1,2})\s*(jan|feb|mar|apr|maj|jun|jul|aug|sep|okt|nov|dec)[a-zåäö]*\.?\s*(20\d{2})?/i);
  if (written) {
    const month = MONTHS[written[2].toLowerCase()];
    const year = written[3] || today.getFullYear();
    return `${year}-${pad(month)}-${pad(written[1])}`;
  }

  return '';
}

// Hittar första klockslaget. Klarar 10:00, 10.00 och "kl 10".
export function findTime(str) {
  if (!str) return '';
  const colon = str.match(/\b([01]?\d|2[0-3])[:.]([0-5]\d)\b/);
  if (colon) return `${pad(colon[1])}:${colon[2]}`;
  const bare = str.match(/\bkl\.?\s*([01]?\d|2[0-3])\b/i);
  if (bare) return `${pad(bare[1])}:00`;
  return '';
}

// Etiketter i säljsystemets mailformat — aldrig ett personnamn.
const LABELS = /^(start|slut|plats|ämne|beskrivning|resurs|bransch|ort|omsättning|kundnummer|företag\/person|möteskommentar|antal anställda)$/i;

function looksLikeName(line) {
  if (!line || line.length < 2 || line.length > 60) return false;
  if (line.includes('@') || /\d/.test(line)) return false;
  if (LABELS.test(line)) return false;
  return /^[A-ZÅÄÖ][\wÀ-ÿ'’-]*(\s+[\wÀ-ÿ'’-]+){0,3}$/.test(line);
}

// Hittar personnamnet kring en e-postrad. I ett signaturblock står namnet
// överst och rolltiteln under, så flerordiga kandidater längst från e-posten
// väljs före enordiga precis intill den.
function pickName(lines, emailIdx) {
  const before = [];
  for (let i = emailIdx - 1; i >= Math.max(0, emailIdx - 4); i--) {
    if (looksLikeName(lines[i])) before.push(lines[i]);
  }
  const multiWord = before.filter(n => n.includes(' '));
  if (multiWord.length) return multiWord[multiWord.length - 1];
  if (before.length) return before[before.length - 1];

  for (let i = emailIdx + 1; i <= Math.min(lines.length - 1, emailIdx + 2); i++) {
    if (looksLikeName(lines[i])) return lines[i];
  }
  return '';
}

// Tolkar ett inbokningsmail till det som behövs för en kalenderbokning.
// Fungerar både på säljsystemets etikettformat (Start / Företag/Person /
// Beskrivning) och på friare mejltext.
export function parseMeetingBooking(str) {
  if (!str || !str.trim()) return null;
  const lines = str.split(/\r?\n/).map(l => l.trim());
  const result = { customerName: '', contactName: '', contactEmail: '', date: '', time: '' };

  // Etikettformat: värdet står på raden efter etiketten
  let startLine = '';
  for (let i = 0; i < lines.length; i++) {
    const label = lines[i];
    const value = lines[i + 1] || '';
    if (label === 'Start' && !startLine) startLine = value.replace(/\s*On\s*$/, '').trim();
    if (label === 'Företag/Person' && !result.customerName) result.customerName = value;
  }

  if (startLine) {
    result.date = findDate(startLine);
    result.time = findTime(startLine);
  }

  // Kontaktperson: e-post först, namnet söks i närmaste rader omkring den
  const emailIdx = lines.findIndex(l => EMAIL_RE.test(l));
  if (emailIdx >= 0) {
    result.contactEmail = lines[emailIdx].match(EMAIL_RE)[0].toLowerCase();
    result.contactName = pickName(lines, emailIdx);
  }

  // Outlook-format "Namn <adress>" någonstans i texten
  if (!result.contactEmail || !result.contactName) {
    const angle = str.match(/([^<>\n;]+?)\s*<([\w.+-]+@[\w-]+\.[\w.]+)>/);
    if (angle) {
      if (!result.contactEmail) result.contactEmail = angle[2].toLowerCase();
      if (!result.contactName && !angle[1].includes('@')) result.contactName = angle[1].trim();
    }
  }

  // Fritext: leta datum och tid i hela mailet om etiketten saknades
  if (!result.date) result.date = findDate(str);
  if (!result.time) result.time = findTime(str);

  // Kundnamn saknas — gissa på e-postdomänen
  if (!result.customerName && result.contactEmail) {
    const domain = result.contactEmail.split('@')[1] || '';
    const base = domain.split('.')[0];
    if (base && !/^(gmail|hotmail|outlook|live|icloud|yahoo|telia|bredband2?)$/i.test(base)) {
      result.customerName = base.charAt(0).toUpperCase() + base.slice(1);
    }
  }

  return Object.values(result).some(Boolean) ? result : null;
}

// ── Mötesobjekt ───────────────────────────────────────────────────────────────

// Delar upp datum/tid i rena tal utan att gå via Date, så att inget skiftas
// av webbläsarens tidszon.
function splitDateTime(date, time) {
  const [y, m, d] = (date || '').split('-').map(Number);
  const [hh, mm] = (time || '').split(':').map(Number);
  return { y, m, d, hh, mm };
}

function addMinutes({ y, m, d, hh, mm }, minutes) {
  const base = new Date(Date.UTC(y, m - 1, d, hh, mm));
  base.setUTCMinutes(base.getUTCMinutes() + minutes);
  return {
    y: base.getUTCFullYear(), m: base.getUTCMonth() + 1, d: base.getUTCDate(),
    hh: base.getUTCHours(), mm: base.getUTCMinutes(),
  };
}

function toLocalIso({ y, m, d, hh, mm }) {
  return `${y}-${pad(m)}-${pad(d)}T${pad(hh)}:${pad(mm)}:00`;
}

function toIcsLocal({ y, m, d, hh, mm }) {
  return `${y}${pad(m)}${pad(d)}T${pad(hh)}${pad(mm)}00`;
}

// Valda kontakter först, sedan fritextmottagaren. En adress bjuds bara in en gång.
function mergeAttendees(contacts, contactName, contactEmail) {
  const out = [];
  const seen = new Set();
  const add = (name, email, contactId) => {
    const key = String(email || '').trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push({ name: name || email, email: email.trim(), ...(contactId ? { contactId } : {}) });
  };
  for (const c of contacts) add(c.name, c.email, c.contactId);
  add(contactName, contactEmail);
  return out;
}

// Bygger hela mötet: ämne, tider, deltagare och brödtext i HTML + ren text.
export function buildFlatboxMeeting({
  customerName,
  contactName,
  contactEmail,
  date,
  time,
  durationMinutes = MEETING_DEFAULT_MINUTES,
  signatureHtml = '',
  signatureText = '',
  senderName = '',
  roomName = '',
  roomEmail = '',
  isTeams = true,
  // Kontakter hos kunden som ska bjudas in, utöver fritextmottagaren:
  // [{ name, email, contactId }].
  contacts = [],
}) {
  const start = splitDateTime(date, time);
  const end = addMinutes(start, durationMinutes);

  const subject = flatboxSubject(customerName || contactName);
  const { bodyHtml, bodyText } = buildFlatboxBody({
    text: flatboxStandardText(senderName), signatureHtml, signatureText,
  });

  // Rummet bokas som resurs i Outlook när det har en adress. Ett namn utan
  // adress blir bara text i platsfältet — ingen rumsbokning.
  const room = (roomName || roomEmail)
    ? { name: roomName || roomEmail, email: roomEmail }
    : null;
  const location = [room?.name, isTeams ? 'Microsoft Teams-möte' : '']
    .filter(Boolean).join(' · ');

  return {
    subject,
    customerName,
    contactName,
    contactEmail,
    date,
    time,
    durationMinutes,
    bodyHtml,
    bodyText,
    room,
    isTeams,
    location,
    startLocal: toLocalIso(start),
    endLocal: toLocalIso(end),
    icsStart: toIcsLocal(start),
    icsEnd: toIcsLocal(end),
    attendees: mergeAttendees(contacts, contactName, contactEmail),
  };
}

// ── .ics-export ───────────────────────────────────────────────────────────────

// Antal oktetter i UTF-8. Gränsen i RFC 5545 räknas i oktetter, inte tecken,
// och å/ä/ö tar två — därför räcker det inte att räkna strängens längd.
function octets(str) {
  let n = 0;
  for (const ch of str) {
    const c = ch.codePointAt(0);
    n += c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
  }
  return n;
}

// Radbrytning enligt RFC 5545 — max 75 oktetter per rad, fortsättningsrader
// inleds med ett mellanslag. Bryter aldrig mitt i ett tecken.
function foldLine(line) {
  if (octets(line) <= 75) return line;
  const out = [];
  let current = '';
  let bytes = 0;
  for (const ch of line) {
    const size = octets(ch);
    if (bytes + size > 75) {
      out.push(current);
      current = ' ' + ch;
      bytes = 1 + size;
    } else {
      current += ch;
      bytes += size;
    }
  }
  if (current) out.push(current);
  return out.join('\r\n');
}

function icsEscape(str) {
  return String(str || '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

// Europe/Stockholm som VTIMEZONE, så att tiden blir rätt oavsett vilken
// tidszon datorn som öppnar filen står i.
const VTIMEZONE = [
  'BEGIN:VTIMEZONE',
  'TZID:Europe/Stockholm',
  'BEGIN:DAYLIGHT',
  'TZOFFSETFROM:+0100',
  'TZOFFSETTO:+0200',
  'TZNAME:CEST',
  'DTSTART:19700329T020000',
  'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU',
  'END:DAYLIGHT',
  'BEGIN:STANDARD',
  'TZOFFSETFROM:+0200',
  'TZOFFSETTO:+0100',
  'TZNAME:CET',
  'DTSTART:19701025T030000',
  'RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU',
  'END:STANDARD',
  'END:VTIMEZONE',
];

// `uid` och `stamp` skickas in utifrån så att funktionen blir förutsägbar.
// Med includeAttendees=false blir filen en ren kalenderreservation (PUBLISH)
// utan mottagare, istället för en mötesinbjudan (REQUEST).
// `method` styr METHOD-raden separat: PUBLISH med deltagare importeras av
// Outlook som ett möte att granska och skicka själv (Nytt från bokningsmail).
export function buildIcs(meeting, { organizerName, organizerEmail, uid, stamp, includeAttendees = true, method }) {
  const attendeeLines = includeAttendees
    ? meeting.attendees.map(a =>
        `ATTENDEE;ROLE=REQ-PARTICIPANT;PARTSTAT=NEEDS-ACTION;RSVP=TRUE;CN=${icsEscape(a.name)}:mailto:${a.email}`
      )
    : [];

  // Utan avsändaradress blir raden `ORGANIZER;CN=:mailto:` — en ogiltig
  // egenskap som får Outlook att avvisa hela filen. Bättre att utelämna den.
  const organizerLines = organizerEmail
    ? [`ORGANIZER${organizerName ? `;CN=${icsEscape(organizerName)}` : ''}:mailto:${organizerEmail}`]
    : [];

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//LagbasN//FLATBOX-bokning//SV',
    'CALSCALE:GREGORIAN',
    `METHOD:${method || (includeAttendees ? 'REQUEST' : 'PUBLISH')}`,
    ...VTIMEZONE,
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART;TZID=${MEETING_TIMEZONE}:${meeting.icsStart}`,
    `DTEND;TZID=${MEETING_TIMEZONE}:${meeting.icsEnd}`,
    `SUMMARY:${icsEscape(meeting.subject)}`,
    `DESCRIPTION:${icsEscape(meeting.bodyText)}`,
    `X-ALT-DESC;FMTTYPE=text/html:${icsEscape(meeting.bodyHtml)}`,
    ...(meeting.location ? [`LOCATION:${icsEscape(meeting.location)}`] : []),
    ...organizerLines,
    ...attendeeLines,
    'SEQUENCE:0',
    'STATUS:CONFIRMED',
    'TRANSP:OPAQUE',
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return lines.map(foldLine).join('\r\n') + '\r\n';
}

export function icsFilename(meeting) {
  const safe = (meeting.customerName || 'mote')
    .replace(/[^\wÀ-ÿ -]/g, '').replace(/\s+/g, '-').slice(0, 40);
  return `FLATBOX-${safe || 'mote'}-${meeting.date || ''}.ics`;
}

export function downloadIcs(icsText, filename) {
  const blob = new Blob([icsText], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
